import { useEffect, useMemo, useRef, useState } from 'react'
import { postTableSearchCounts } from '../fetch/post-table-search-counts'
import { visibleColumnOrder, type TableLayout } from '../layout'
import type { TablePage, TableQuery, TableSearchCounts } from '../schemas'

export type TableMatch = { rowId: string; columnId: string; offset: number }

/** Navigation uses server-provided spans, including rows outside the mounted virtual viewport. */
export function useTableSearch({
  tableId,
  data,
  query,
  displayedQuery,
  loading,
  error,
  layout,
  refreshVersion,
  onQueryChange,
}: {
  tableId: string
  data: TablePage | null
  query: TableQuery
  displayedQuery: TableQuery
  loading: boolean
  error: string | null
  layout: TableLayout
  refreshVersion: string
  onQueryChange: (query: TableQuery) => void
}) {
  const scope = JSON.stringify({ ...query, offset: 0 })
  const displayedScope = JSON.stringify({ ...displayedQuery, offset: 0 })
  const ready = Boolean(data?.table.id === tableId && scope === displayedScope && query.search?.trim())
  const [countResult, setCountResult] = useState<{ scope: string; counts: TableSearchCounts } | null>(null)
  const [countError, setCountError] = useState(false)
  const [retry, setRetry] = useState(0)
  const countKey = JSON.stringify([tableId, scope, refreshVersion, retry])
  useEffect(() => {
    if (!ready) return
    const controller = new AbortController()
    setCountError(false)
    const request: TableQuery = JSON.parse(scope)
    postTableSearchCounts(tableId, request, controller.signal)
      .then((counts) => {
        if (!controller.signal.aborted) setCountResult({ scope: countKey, counts })
      })
      .catch(() => {
        if (!controller.signal.aborted) setCountError(true)
      })
    return () => controller.abort()
  }, [tableId, scope, ready, countKey])
  const counts = countResult?.scope === countKey ? countResult.counts : null
  const order = visibleColumnOrder([...(data?.document_based ? ['row'] : []), ...(data?.columns.map((c) => c.id) ?? [])], layout)
  const orderKey = order.join(',')
  const matches = useMemo(() => {
    const result: TableMatch[] = []
    for (const row of data?.rows ?? []) {
      for (const columnId of orderKey.split(',')) {
        const offsets = columnId === 'row' ? row.filename_search_offsets : row.cells.find((cell) => cell.column_id === columnId)?.search_offsets
        for (const offset of offsets ?? []) result.push({ rowId: row.id, columnId, offset })
      }
    }
    return result
  }, [data, orderKey])
  const navigationKey = JSON.stringify([tableId, scope, refreshVersion, orderKey])
  const visitedPages = useRef<{ key: string; prefixes: Map<number, number> }>({ key: navigationKey, prefixes: new Map() })
  const [selection, setSelection] = useState<{ key: string; offset: number; index: number; prefix?: number } | null>(null)
  const selected = selection?.key === navigationKey && selection.offset === displayedQuery.offset ? selection : null
  let index = Math.min(selected?.index ?? 0, Math.max(0, matches.length - 1))
  if (selected?.index === -1) index = matches.length - 1
  let prefix = counts?.page_match_offsets[Math.floor(displayedQuery.offset / displayedQuery.limit)] ?? selected?.prefix
  if (prefix === undefined && visitedPages.current.key === navigationKey) prefix = visitedPages.current.prefixes.get(displayedQuery.offset)
  if (prefix === undefined && displayedQuery.offset === 0) prefix = 0
  const searching = loading || !ready || query.offset !== displayedQuery.offset
  const activeMatch = !searching ? matches[index] ?? null : null
  const ordinal = activeMatch && prefix !== undefined ? prefix + index + 1 : null
  const busy = searching || Boolean(error)
  const hasMore = data?.has_more ?? (data?.total != null && displayedQuery.offset + displayedQuery.limit < data.total)
  const canPrevious = !busy && matches.length > 0 && (index > 0 || displayedQuery.offset > 0 || Boolean(counts?.total_matches))
  const canNext = !busy && matches.length > 0 && (index < matches.length - 1 || hasMore || Boolean(counts?.total_matches))
  const step = (direction: -1 | 1) => {
    if (direction === -1 ? !canPrevious : !canNext) return
    if (visitedPages.current.key !== navigationKey) visitedPages.current = { key: navigationKey, prefixes: new Map() }
    if (prefix !== undefined) visitedPages.current.prefixes.set(displayedQuery.offset, prefix)
    const nextIndex = index + direction
    if (nextIndex >= 0 && nextIndex < matches.length) {
      setSelection({ key: navigationKey, offset: displayedQuery.offset, index: nextIndex, prefix })
      return
    }
    let offset = displayedQuery.offset + direction * displayedQuery.limit
    if (offset < 0 && counts) offset = Math.floor((counts.total_rows - 1) / displayedQuery.limit) * displayedQuery.limit
    if (direction === 1 && !hasMore) offset = 0
    if (offset === displayedQuery.offset) {
      setSelection({ key: navigationKey, offset, index: direction === 1 ? 0 : -1, prefix })
      return
    }
    let nextPrefix: number | undefined
    if (offset === 0) nextPrefix = 0
    else if (direction === 1 && prefix !== undefined) nextPrefix = prefix + matches.length
    setSelection({
      key: navigationKey,
      offset,
      index: direction === 1 ? 0 : -1,
      prefix: nextPrefix,
    })
    onQueryChange({ ...query, offset })
  }
  return {
    activeMatch,
    ordinal,
    total: counts?.total_matches ?? null,
    totalRows: counts?.total_rows ?? data?.total ?? null,
    searching,
    noMatches: ready && !loading && data?.rows.length === 0,
    countError,
    error,
    canPrevious,
    canNext,
    step,
    retryCount: () => setRetry((value) => value + 1),
  }
}
